import { Switch } from "@/components/ui/switch";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import NumberSlider from "./NumberSlider";
import CountryTaxPicker from "./CountryTaxPicker";

export interface RetirementFormState {
  currentAge: number;
  targetAge: number;
  currentSavings: number;
  monthlyContribution: number;
  monthlyExpenses: number;
  annualReturnPct: number;
  inflationPct: number;
  shariahMode: boolean;
  countryCode: string;
  customTaxRatePct: number;
  showAfterTax: boolean;
}


interface Props {
  state: RetirementFormState;
  onChange: (s: RetirementFormState) => void;
  onReset: () => void;
}

const RetirementInputs = ({ state, onChange, onReset }: Props) => {
  const set = <K extends keyof RetirementFormState>(k: K, v: RetirementFormState[K]) =>
    onChange({ ...state, [k]: v });

  const targetMin = state.currentAge + 1;
  const targetValue = Math.max(targetMin, state.targetAge);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold">Your plan</h2>
        <button
          type="button"
          onClick={onReset}
          className="text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          Reset
        </button>
      </div>

      <NumberSlider
        label="Current age"
        value={state.currentAge}
        min={12}
        max={75}
        step={1}
        suffix="yrs"
        onChange={(v) => {
          const next = { ...state, currentAge: v };
          if (next.targetAge <= v) next.targetAge = v + 1;
          onChange(next);
        }}
      />
      <NumberSlider
        label="Target retirement age"
        value={targetValue}
        min={targetMin}
        max={80}
        step={1}
        suffix="yrs"
        onChange={(v) => set("targetAge", v)}
      />
      <NumberSlider
        label="Current savings"
        value={state.currentSavings}
        min={0}
        max={1_000_000}
        step={1_000}
        prefix="$"
        hardMax={5_000_000}
        onChange={(v) => set("currentSavings", v)}
      />
      <NumberSlider
        label="Monthly contribution"
        value={state.monthlyContribution}
        min={0}
        max={10_000}
        step={50}
        prefix="$"
        hardMax={50_000}
        onChange={(v) => set("monthlyContribution", v)}
      />
      <NumberSlider
        label="Monthly expenses in retirement"
        value={state.monthlyExpenses}
        min={200}
        max={20_000}
        step={50}
        prefix="$"
        hardMax={50_000}
        onChange={(v) => set("monthlyExpenses", v)}
      />
      <NumberSlider
        label="Expected annual return"
        value={state.annualReturnPct}
        min={1}
        max={100}
        step={0.1}
        suffix="%"
        decimals={1}
        onChange={(v) => set("annualReturnPct", v)}
      />
      <NumberSlider
        label="Inflation rate"
        value={state.inflationPct}
        min={0}
        max={10}
        step={0.1}
        suffix="%"
        decimals={1}
        onChange={(v) => set("inflationPct", v)}
      />

      <CountryTaxPicker
        countryCode={state.countryCode}
        customTaxRatePct={state.customTaxRatePct}
        onCountryChange={(c) => set("countryCode", c)}
        onCustomRateChange={(v) => set("customTaxRatePct", v)}
      />

      <div className="pt-3 mt-1">
        <ToggleRow
          label="Show after-tax target"
          hint="ON: your monthly expenses are already after tax — the 25× target stays as-is. OFF: we gross up the 25× target by your country's withdrawal tax rate so 4% withdrawals net the spending you entered."
          checked={state.showAfterTax}
          onChange={(v) => set("showAfterTax", v)}
        />
      </div>

      <div className="pt-3 mt-1 border-t border-border">
        <ToggleRow
          label="Deduct annual Zakat (above Nisab)"
          hint="For Shariah-aligned planning. Zakat (2.5% lunar ≈ 2.58% solar) is only deducted in years where your portfolio is at or above the Nisab threshold — the value of 85 g of gold, projected forward at ~6%/yr gold appreciation. Below Nisab, nothing is deducted."
          checked={state.shariahMode}
          onChange={(v) => set("shariahMode", v)}
        />
      </div>
    </div>
  );
};

const ToggleRow = ({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-sm font-medium text-foreground/90">{label}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={`${label} info`}
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
          {hint}
        </TooltipContent>
      </Tooltip>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
  </div>
);

export default RetirementInputs;
